import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Wallet, Download, DollarSign, Users, ClipboardCheck, FileText } from "lucide-react";

export default function Payouts() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = () => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    api.get("/reports/payouts", { params }).then((r) => setData(r.data));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/reports/payouts.csv", {
        params: { ...(start && { start }), ...(end && { end }) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payouts_${start || "all"}_${end || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const downloadStatement = async (cleaner) => {
    try {
      const res = await api.get(`/reports/payouts/${cleaner.cleaner_id}/statement.pdf`, {
        params: { ...(start && { start }), ...(end && { end }) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `statement_${cleaner.name.replace(/ /g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Statement for ${cleaner.name} downloaded`);
    } catch (e) {
      toast.error("Could not generate statement");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">
            Payroll
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Cleaner Payouts</h1>
        </div>
        <Button onClick={exportCsv} disabled={downloading} data-testid="export-csv-button">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-44"
              data-testid="payout-start-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-44"
              data-testid="payout-end-input"
            />
          </div>
          <Button onClick={load} data-testid="apply-filter-button">
            Apply
          </Button>
          {(start || end) && (
            <Button
              variant="ghost"
              onClick={() => {
                setStart("");
                setEnd("");
                setTimeout(load, 0);
              }}
            >
              Clear
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Payouts are based on <b>completed</b> jobs. Each property's cleaning cost is split evenly
          between assigned cleaners.
        </p>
      </Card>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <DollarSign className="h-5 w-5 text-primary mb-2" strokeWidth={1.7} />
          <div className="font-display text-3xl font-semibold">
            ${data?.total?.toLocaleString() ?? "0"}
          </div>
          <p className="text-sm text-muted-foreground mt-1">Total payouts</p>
        </Card>
        <Card className="p-5">
          <Users className="h-5 w-5 text-sky-600 mb-2" strokeWidth={1.7} />
          <div className="font-display text-3xl font-semibold">{data?.summary?.length ?? 0}</div>
          <p className="text-sm text-muted-foreground mt-1">Cleaners paid</p>
        </Card>
        <Card className="p-5">
          <ClipboardCheck className="h-5 w-5 text-emerald-600 mb-2" strokeWidth={1.7} />
          <div className="font-display text-3xl font-semibold">{data?.job_count ?? 0}</div>
          <p className="text-sm text-muted-foreground mt-1">Completed jobs</p>
        </Card>
      </div>

      {/* Per-cleaner summary */}
      <Card className="p-6 mb-6">
        <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <Wallet className="h-5 w-5 text-primary" /> Per-cleaner summary
        </h2>
        {!data || data.summary.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No completed jobs in this range yet.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cleaner</TableHead>
                <TableHead className="text-right">Jobs</TableHead>
                <TableHead className="text-right">Payout</TableHead>
                <TableHead className="text-right">Statement</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.summary.map((s) => (
                <TableRow key={s.cleaner_id} data-testid={`payout-row-${s.cleaner_id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="text-right">{s.jobs}</TableCell>
                  <TableCell className="text-right font-semibold">
                    ${s.amount.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadStatement(s)}
                      data-testid={`statement-${s.cleaner_id}`}
                    >
                      <FileText className="h-4 w-4 mr-1.5" /> PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Line items */}
      {data && data.line_items.length > 0 && (
        <Card className="p-6">
          <h2 className="font-display text-lg font-semibold mb-4">Detailed line items</h2>
          <div className="max-h-96 overflow-y-auto scrollbar-thin">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Cleaner</TableHead>
                  <TableHead className="text-right">Share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.line_items.map((li, i) => (
                  <TableRow key={i}>
                    <TableCell className="whitespace-nowrap">{li.date}</TableCell>
                    <TableCell>{li.property}</TableCell>
                    <TableCell>{li.cleaner}</TableCell>
                    <TableCell className="text-right">${li.share.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
